import { Outlet } from "react-router-dom";
import { useState, useEffect, useRef, useContext } from "react";
import { useThemeDetector } from "../../hooks/useThemeDetector";
import { useCreateReducer } from "../../hooks/useCreateReducer";
import { ThemeInitialState } from "../../interfaces/app.interface";
import { ThemeContext } from "../../store/theme.store";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { Login } from "../login/login";
import { Toaster } from "react-hot-toast";
import { SidebarProvider } from "@/components/ui/sidebar";
import { BANNER_DISMISS_KEY, TOASTER_CONFIG } from "@/constants/app.constant";
import RequireConsent from "../../routes/require-consent";
import { useAgreementConsent } from "@/hooks/useAgreementConsent";
import useConfigService from "@/services/config.service";
import chopLogo from "../../assets/chop_logo-2.svg";
import { CONSENT_CHECKED_KEY } from "@/constants/consent.constants";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { CHAT_INITIAL_STATE, ChatContext } from "@/store/chat.store";
import { ChatInitialState } from "@/interfaces/chat.interface";
import { useTokenUsageApi } from "@/services/token.service";
import { CREDITS_INITIAL_STATE, CreditsContext } from "@/store/credits.store";
import { CreditsState } from "@/interfaces/credits.interface";
import { UserGroupsProvider } from "@/context/user-groups.context";


const Layout = () => {
  const isDarkTheme = useThemeDetector();
  const contextValue = useCreateReducer<ThemeInitialState>({
    initialState: { isDarkTheme },
  });

  const chatContextValue = useCreateReducer<ChatInitialState>({
    initialState: CHAT_INITIAL_STATE,
  });

  const creditContextValue = useCreateReducer<CreditsState>({
    initialState: CREDITS_INITIAL_STATE,
  });

  const { state } = contextValue;

  const { state : creditState, dispatch: creditDispatch } = creditContextValue;

  const { accounts } = useMsal();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isCheckingConsent, setIsCheckingConsent] = useState(false);
  const { agree } = useAgreementConsent();
  const { initializeTokenData } = useTokenUsageApi();
  const { fetchTermsAndConditions } = useConfigService();

  const hasRun = useRef(false);

  useEffect(() => {
    const handleLogoutStart = () => {
      setIsLoggingOut(true);
      // Clear the consent check flag on logout
      sessionStorage.removeItem(CONSENT_CHECKED_KEY);
      sessionStorage.removeItem(BANNER_DISMISS_KEY);

    };
    const handleLogoutEnd = () => setIsLoggingOut(false);

    window.addEventListener('logout-start', handleLogoutStart);
    window.addEventListener('logout-end', handleLogoutEnd);

    return () => {
      window.removeEventListener('logout-start', handleLogoutStart);
      window.removeEventListener('logout-end', handleLogoutEnd);
    };
  }, []);

  // Check consent only once per session
  useEffect(() => {
    if (!accounts || accounts.length === 0) {
      return;
    }

    // Check if we've already checked consent in this session
    const hasChecked = sessionStorage.getItem(CONSENT_CHECKED_KEY);
    if (hasChecked) {
      return;
    }

    const checkConsent = async () => {
      try {
        setIsCheckingConsent(true);
        const response = await fetchTermsAndConditions();

        if (response?.data) {
          if (!response.data.should_display) {
            // Terms don't need to be displayed, mark as agreed
            agree(response.data.version, true);
          } else {
            // Terms need to be displayed
            agree(response.data.version, false);
          }
        }

        // Mark that we've checked consent for this session
        sessionStorage.setItem(CONSENT_CHECKED_KEY, "true");
      } catch (error) {
        console.error("Error checking consent:", error);
        sessionStorage.setItem(CONSENT_CHECKED_KEY, "false");
      } finally {
        setIsCheckingConsent(false);
      }
    };

    checkConsent();
  }, [accounts]);

  useEffect(() => {
    // Only run after MSAL has authenticated user
    if (accounts.length > 0 && !hasRun.current) {
      hasRun.current = true;

      initializeTokenData()
        .then((groups_data) => {
          creditDispatch({
            field: "model_groups",
            value: groups_data.data,
          });
        })
        .catch((err) => console.error("Token usage init failed", err));
    }
  }, [accounts]);

  // Show loading while checking consent
  if (accounts && accounts.length && isCheckingConsent) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <img
            src={chopLogo}
            alt="Children's Hospital of Philadelphia"
            className="h-16 w-auto mx-auto mb-4"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking your account...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {accounts && accounts.length && !isLoggingOut ? (
        <AuthenticatedTemplate>
          <UserGroupsProvider>
          <RequireConsent>
            <>
              <ThemeContext.Provider value={contextValue}>
                <ChatContext.Provider value={chatContextValue}>
                <CreditsContext.Provider value={creditContextValue}>
                  <SidebarProvider>
                    <AppSidebar />
                    <div
                      className={
                        (state.isDarkTheme ? "dark " : "") +
                        "layout-wrapper h-full w-full text-white overflow-x-hidden"
                      }
                    >
                      <div className="page-wrapper bg-page w-full">
                        <Outlet />
                      </div>
                      </div>
                    </SidebarProvider>
                    </CreditsContext.Provider>
                </ChatContext.Provider>
              </ThemeContext.Provider>
              <Toaster
                position="top-right"
                reverseOrder={false}
                toastOptions={TOASTER_CONFIG}
              />
            </>
          </RequireConsent>
          </UserGroupsProvider>
        </AuthenticatedTemplate>
      ) : (
        <UnauthenticatedTemplate>
          {isLoggingOut ? (
            <div className="flex items-center justify-center h-full bg-gray-100">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Signing out...</p>
              </div>
            </div>
          ) : (
            <Login />
          )}
        </UnauthenticatedTemplate>
      )}
    </>
  );
};

export default Layout;